// Shared "pick a saved medication" data + list UI (MEDICATION_BATCH_LOGGING_PLAN.md
// Task 4), extracted out of QuickMedLog so both the dashboard quick-log modal and
// the SuggestionCard "+ Add another medication" flow build their picker off one
// source of truth for "this patient's saved, active medications, searchable, with
// overdose red-flagging."

import { useEffect, useMemo, useState } from "react";
import type { MedUnit, MedFormUnit, OverdoseSeverity, Regimen } from "@medassist/shared";
import { formatMedStrength, isMedItemActive } from "@medassist/shared";
import {
  getRegimens,
  listEvents,
  getMedicationOverdoseCheck,
  ApiError,
} from "../lib/api.js";
import { relativeTime } from "../lib/time.js";
import { ChevronDownIcon, LayersIcon } from "./icons.js";

// One selectable medication, flattened from the patient's regimens.
export interface MedChoice {
  regimenId: string;
  lineId: string;
  name: string;
  dose?: number;
  unit?: MedUnit;
  pillStrength?: number;
  pillForm?: MedFormUnit;
  pillHalfSteps?: boolean;
  pillQuarterSteps?: boolean;
  hasIngredients: boolean;
  strength: string; // formatted, for display
  fromAsNeeded: boolean;
  intendedFor?: string; // free text, e.g. "acid reflux" — searchable (MEDICATION_QUICK_LOG search)
  lastDoseAt?: string; // ISO of the most recent non-missed dose, for "last dose 2 days ago"
  /** Would taking this dose right now trip the overdose guard, given actual recent
   *  history (MEDICATION_OVERDOSE_PLAN.md)? null = safe or unevaluated. */
  overdose?: OverdoseSeverity | null;
}

/** A saved scheduled regimen offered as a one-tap group in the quick-log picker:
 *  selecting it logs every active med in the regimen at once (dashboard "log
 *  medication" regimen groups). Built only for `kind: "scheduled"` regimens with
 *  2+ active items — a one-med "group" is just its flat row. `items` reuse the
 *  flat `MedChoice` shape so strength / overdose flag / last-dose carry through. */
export interface RegimenGroupChoice {
  regimenId: string;
  name: string;
  items: MedChoice[];
}

/** Loads this patient's saved regimens + recent dose history and flattens them into
 *  a deduped, ranked `MedChoice[]` (as-needed first, then scheduled; most-frequently-
 *  logged first). Same data-fetch this used to do inline in QuickMedLog. */
export function useMedChoices(
  patientId: string,
  /** false skips the server overdose check (and its red flags) — for backfilled doses
   *  that were taken days ago, where "may exceed a safe dose if taken now" is moot. */
  includeOverdose = true
): {
  choices: MedChoice[];
  regimenGroups: RegimenGroupChoice[];
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regimens, setRegimens] = useState<Regimen[]>([]);
  // Count of logged doses per medication name (lowercased), used to rank the
  // pick list by how often the user actually logs each med.
  const [logCounts, setLogCounts] = useState<Map<string, number>>(new Map());
  // Most recent non-missed dose per medication name (lowercased) — "last dose 2 days ago".
  const [lastDoseAt, setLastDoseAt] = useState<Map<string, string>>(new Map());
  // Per-medication overdose severity, keyed by "regimenId:lineId" (server-evaluated).
  const [overdoseByKey, setOverdoseByKey] = useState<Map<string, OverdoseSeverity | null>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [regs, takenEvents, asNeededEvents, overdose] = await Promise.all([
          getRegimens(patientId),
          listEvents(patientId, "medication_taken"),
          listEvents(patientId, "as_needed_med"),
          // Best-effort: a failed check just means no red highlight this load.
          includeOverdose
            ? getMedicationOverdoseCheck(patientId).catch(() => new Map<string, OverdoseSeverity | null>())
            : Promise.resolve(new Map<string, OverdoseSeverity | null>()),
        ]);
        if (cancelled) return;
        setRegimens(regs);
        setOverdoseByKey(overdose);
        const counts = new Map<string, number>();
        const lastDose = new Map<string, string>();
        for (const ev of [...takenEvents, ...asNeededEvents]) {
          if (ev.deleted) continue;
          const p = ev.payload as Record<string, unknown>;
          if (p.missed === true) continue; // not actually taken
          const name = typeof p.name === "string" ? p.name.trim().toLowerCase() : "";
          if (!name) continue;
          counts.set(name, (counts.get(name) ?? 0) + 1);
          const takenAt = typeof p.takenAt === "string" ? p.takenAt : ev.occurredAt;
          const prior = lastDose.get(name);
          if (!prior || Date.parse(takenAt) > Date.parse(prior)) lastDose.set(name, takenAt);
        }
        setLogCounts(counts);
        setLastDoseAt(lastDose);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Couldn't load medications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // Flatten active items from all regimens into a deduped choice list: as-needed
  // (kind=as_needed_inventory) first, then scheduled meds. Dedupe by name, keeping
  // the as-needed entry when a med appears in both.
  const choices = useMemo<MedChoice[]>(() => {
    const byName = new Map<string, MedChoice>();
    const add = (reg: Regimen, fromAsNeeded: boolean) => {
      for (const it of reg.items) {
        if (!isMedItemActive(it)) continue;
        const key = it.name.trim().toLowerCase();
        if (!key) continue;
        const existing = byName.get(key);
        // Prefer the as-needed entry over a scheduled duplicate.
        if (existing && !(fromAsNeeded && !existing.fromAsNeeded)) continue;
        const nameKey = it.name.trim().toLowerCase();
        byName.set(key, {
          regimenId: reg.regimenId,
          lineId: it.lineId,
          name: it.name,
          dose: it.dose,
          unit: it.unit,
          pillStrength: it.pillStrength,
          pillForm: it.pillForm,
          pillHalfSteps: it.pillHalfSteps,
          pillQuarterSteps: it.pillQuarterSteps,
          hasIngredients: !!it.ingredients?.length,
          strength: formatMedStrength(it),
          fromAsNeeded,
          intendedFor: it.intendedFor,
          lastDoseAt: lastDoseAt.get(nameKey),
          overdose: overdoseByKey.get(`${reg.regimenId}:${it.lineId}`) ?? null,
        });
      }
    };
    // as-needed first so it wins the dedupe
    regimens.filter((r) => r.active && r.kind === "as_needed_inventory").forEach((r) => add(r, true));
    regimens.filter((r) => r.active && r.kind === "scheduled").forEach((r) => add(r, false));
    // Most-frequently-logged first, falling back to alphabetical for ties
    // (including meds never logged, which all sit at count 0).
    return [...byName.values()].sort((a, b) => {
      const diff = (logCounts.get(b.name.trim().toLowerCase()) ?? 0) - (logCounts.get(a.name.trim().toLowerCase()) ?? 0);
      return diff !== 0 ? diff : a.name.localeCompare(b.name);
    });
  }, [regimens, logCounts, lastDoseAt, overdoseByKey]);

  // Scheduled regimens with 2+ active items, each expandable/selectable as a group.
  const regimenGroups = useMemo<RegimenGroupChoice[]>(() => {
    const groups: RegimenGroupChoice[] = [];
    for (const reg of regimens) {
      if (!reg.active || reg.kind !== "scheduled") continue;
      const activeItems = reg.items.filter(isMedItemActive);
      if (activeItems.length < 2) continue;
      groups.push({
        regimenId: reg.regimenId,
        name: reg.name,
        items: activeItems.map((it) => ({
          regimenId: reg.regimenId,
          lineId: it.lineId,
          name: it.name,
          dose: it.dose,
          unit: it.unit,
          pillStrength: it.pillStrength,
          pillForm: it.pillForm,
          pillHalfSteps: it.pillHalfSteps,
          pillQuarterSteps: it.pillQuarterSteps,
          hasIngredients: !!it.ingredients?.length,
          strength: formatMedStrength(it),
          fromAsNeeded: false,
          intendedFor: it.intendedFor,
          lastDoseAt: lastDoseAt.get(it.name.trim().toLowerCase()),
          overdose: overdoseByKey.get(`${reg.regimenId}:${it.lineId}`) ?? null,
        })),
      });
    }
    return groups;
  }, [regimens, lastDoseAt, overdoseByKey]);

  return { choices, regimenGroups, loading, error };
}

/** A titled list of medications, either tap-to-select-and-navigate (`onPick`) or
 *  checkbox multi-select (`selected`/`onToggle`) — exactly one mode is supplied. */
export function MedSection({
  title,
  meds,
  onPick,
  selected,
  onToggle,
}: {
  title: string;
  meds: MedChoice[];
  onPick?: (c: MedChoice) => void;
  /** Keys are `${regimenId}:${lineId}`. Checkbox mode when supplied alongside `onToggle`. */
  selected?: Set<string>;
  onToggle?: (c: MedChoice) => void;
}) {
  const checkboxMode = !!selected && !!onToggle;
  return (
    <div className="mb-3">
      <p className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {title}
      </p>
      <ul className="space-y-1">
        {meds.map((c) => {
          // Any fired severity (caution/warning/critical) gets the red treatment —
          // the picker just needs to flag risk, not triage by tier (MEDICATION_OVERDOSE_PLAN.md).
          const flagged = !!c.overdose;
          const key = `${c.regimenId}:${c.lineId}`;
          const isChecked = checkboxMode && selected!.has(key);
          return (
            <li key={key}>
              <button
                onClick={() => (checkboxMode ? onToggle!(c) : onPick?.(c))}
                className={
                  "flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left text-sm " +
                  (flagged
                    ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/50"
                    : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800")
                }
              >
                <span className="flex items-center gap-2">
                  {checkboxMode && (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      className="pointer-events-none h-4 w-4 shrink-0 accent-(--accent)"
                      aria-hidden
                    />
                  )}
                  <span className="font-medium text-gray-900 dark:text-gray-100">{c.name}</span>
                  {c.strength && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{c.strength}</span>
                  )}
                  {c.lastDoseAt && (
                    <span className="ml-auto shrink-0 text-xs text-gray-400 dark:text-gray-500">
                      last dose {relativeTime(c.lastDoseAt)}
                    </span>
                  )}
                </span>
                {flagged && (
                  <span className="text-xs font-medium text-red-600 dark:text-red-400">
                    ⚠️ may exceed a safe dose if taken now
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** One saved regimen in the quick-log picker: a collapsed-by-default row with an
 *  expand toggle that reveals its member meds. Tapping the row either logs the
 *  whole regimen now (`onPick`, single-select) or toggles every member in the
 *  batch (`onToggleGroup`/`onToggleItem`, multi-select). Member keys are
 *  `${regimenId}:${lineId}` — the same keys QuickMedLog's multi-select set uses,
 *  so a regimen and the flat "Scheduled" rows compose without double-counting. */
export function RegimenGroupRow({
  group,
  expanded,
  onToggleExpand,
  onPick,
  selected,
  onToggleGroup,
  onToggleItem,
}: {
  group: RegimenGroupChoice;
  expanded: boolean;
  onToggleExpand: () => void;
  onPick?: (g: RegimenGroupChoice) => void;
  selected?: Set<string>;
  onToggleGroup?: (g: RegimenGroupChoice) => void;
  onToggleItem?: (c: MedChoice) => void;
}) {
  const checkboxMode = !!selected && !!onToggleGroup && !!onToggleItem;
  const keys = group.items.map((i) => `${i.regimenId}:${i.lineId}`);
  const selCount = checkboxMode ? keys.filter((k) => selected!.has(k)).length : 0;
  const allSel = checkboxMode && selCount === keys.length;
  const someSel = checkboxMode && selCount > 0 && !allSel;
  const anyFlagged = group.items.some((i) => !!i.overdose);
  return (
    <li>
      <div
        className={
          "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm " +
          (anyFlagged
            ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40"
            : "border-gray-200 dark:border-gray-800")
        }
      >
        {checkboxMode && (
          <input
            type="checkbox"
            checked={allSel}
            ref={(el) => {
              if (el) el.indeterminate = someSel;
            }}
            onChange={() => onToggleGroup!(group)}
            className="h-4 w-4 shrink-0 accent-(--accent)"
            aria-label={`Select all in ${group.name}`}
          />
        )}
        <button
          onClick={() => (checkboxMode ? onToggleGroup!(group) : onPick?.(group))}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <LayersIcon size={15} className="shrink-0 text-(--accent)" />
          <span className="truncate font-medium text-gray-900 dark:text-gray-100">{group.name}</span>
          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {group.items.length} meds
          </span>
        </button>
        <button
          onClick={onToggleExpand}
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={expanded ? "Hide medications" : "Show medications"}
          aria-expanded={expanded}
        >
          <ChevronDownIcon
            size={16}
            className={"transition-transform " + (expanded ? "" : "-rotate-90")}
          />
        </button>
      </div>
      {expanded && (
        <ul className="mt-1 mb-1 space-y-1 pl-6">
          {group.items.map((c) => {
            const key = `${c.regimenId}:${c.lineId}`;
            return (
              <li key={key}>
                <div
                  className={
                    "flex items-center gap-2 rounded-md px-2 py-1 text-sm " +
                    (c.overdose
                      ? "text-red-700 dark:text-red-300"
                      : "text-gray-700 dark:text-gray-300")
                  }
                >
                  {checkboxMode ? (
                    <input
                      type="checkbox"
                      checked={selected!.has(key)}
                      onChange={() => onToggleItem!(c)}
                      className="h-4 w-4 shrink-0 accent-(--accent)"
                      aria-label={`Select ${c.name}`}
                    />
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                  )}
                  <span className="truncate">{c.name}</span>
                  {c.strength && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">{c.strength}</span>
                  )}
                  {c.overdose && <span className="shrink-0 text-xs">⚠️</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
